import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaArrowLeft, FaArrowRight, FaCheck, FaCog, FaInfo, FaPen, FaPlus, FaTimes, FaTrash } from "react-icons/fa";
import "../styles/TimeTrackingSection.css";
import { apiFetch } from "../utils/api";
import { formatDateWithWeekday } from "../utils/dateFormat";
import { useNotifications } from "./NotificationProvider";

const createDraftId = () => `draft-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const createEmptyEvent = (year) => ({
  _draftId: createDraftId(),
  name: "Neues Event",
  start_date: `${year}-01-01`,
  end_date: `${year}-01-01`,
  allow_multiple_shifts: false,
  entries: [],
});

const createEmptyEntry = (year) => ({
  _draftId: createDraftId(),
  work_date: `${year}-01-01`,
  start_time: "",
  end_time: "",
  earnings_midday: "",
  earnings_evening: "",
  earnings: "",
  notes: "",
});

const parseMoney = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return 0;

  const cleaned = raw.replace(/\s/g, "").replace("€", "");
  const normalized = cleaned.includes(",")
    ? cleaned.replace(/\./g, "").replace(",", ".")
    : cleaned;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatMoney = (value) => `${parseMoney(value).toFixed(2).replace(".", ",")} €`;

const formatMoneyInput = (value) => {
  if (value === "" || value == null) return "";
  return parseMoney(value).toFixed(2).replace(".", ",");
};

const minutesFromTime = (value) => {
  const match = String(value || "").match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
};

export const calculateMinutes = (entry) => {
  const start = minutesFromTime(entry.start_time);
  const end = minutesFromTime(entry.end_time);
  if (start == null || end == null) return 0;
  const adjustedEnd = end < start ? end + 24 * 60 : end;
  return Math.max(0, adjustedEnd - start);
};

const formatHours = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return `${hours}h ${rest}m`;
};

const formatDate = (value) => {
  if (!value) return "";
  return formatDateWithWeekday(value);
};

const getEventFirstDate = (event) => {
  const entryDates = (event.entries || []).map(entry => entry.work_date).filter(Boolean);
  const dates = [event.start_date, ...entryDates].filter(Boolean).sort();
  return dates[0] || "";
};

const eventTouchesYear = (event, year) => {
  const yearText = String(year);
  const dates = [
    event.start_date,
    event.end_date,
    ...(event.entries || []).map(entry => entry.work_date),
  ].filter(Boolean);
  return dates.length ? dates.some(date => String(date).startsWith(yearText)) : true;
};

const normalizeEventForDraft = (event) => ({
  ...event,
  start_date: String(event.start_date || "").slice(0, 10),
  end_date: String(event.end_date || "").slice(0, 10),
  allow_multiple_shifts: Boolean(event.allow_multiple_shifts),
  entries: (event.entries || []).map(entry => ({
    ...entry,
    work_date: String(entry.work_date || "").slice(0, 10),
    start_time: entry.start_time || "",
    end_time: entry.end_time || "",
    earnings_midday: formatMoneyInput(entry.earnings_midday),
    earnings_evening: formatMoneyInput(entry.earnings_evening),
    earnings: formatMoneyInput(entry.earnings),
    notes: entry.notes || "",
  })),
});

const prepareEntryPayload = (entry, splitMode) => {
  const midday = parseMoney(entry.earnings_midday);
  const evening = parseMoney(entry.earnings_evening);
  const payload = {
    work_date: entry.work_date,
    start_time: entry.start_time,
    end_time: entry.end_time,
    shift_name: null,
    notes: entry.notes || "",
  };
  if (splitMode) {
    return {
      ...payload,
      earnings_midday: midday,
      earnings_evening: evening,
      earnings: midday + evening,
    };
  }
  return {
    ...payload,
    earnings: parseMoney(entry.earnings),
  };
};

const TimeTrackingSection = () => {
  const { showNotification } = useNotifications();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [events, setEvents] = useState([]);
  const [editingEventKey, setEditingEventKey] = useState(null);
  const [eventDrafts, setEventDrafts] = useState({});
  const [settingsEventKey, setSettingsEventKey] = useState(null);
  const [notesEntry, setNotesEntry] = useState(null);
  const [notesDraft, setNotesDraft] = useState("");
  const [notesReadOnly, setNotesReadOnly] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiFetch("/api/zeit/events");
      setEvents(data);
    } catch (err) {
      console.error(err);
      if (!err.sessionExpired) showNotification("error", err.message);
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const visibleEvents = useMemo(
    () => events
      .filter(event => eventTouchesYear(event, year))
      .sort((a, b) => getEventFirstDate(a).localeCompare(getEventFirstDate(b))),
    [events, year]
  );

  const startEditEvent = (event) => {
    const key = event.id || event._draftId;
    setEditingEventKey(key);
    setEventDrafts(prev => ({ ...prev, [key]: normalizeEventForDraft(event) }));
  };

  const cancelEditEvent = (event) => {
    const key = event.id || event._draftId;
    if (event._draftId) {
      setEvents(prev => prev.filter(item => item._draftId !== event._draftId));
    }
    setEditingEventKey(null);
    setSettingsEventKey(null);
    setEventDrafts(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const updateDraft = (eventKey, updater) => {
    setEventDrafts(prev => ({
      ...prev,
      [eventKey]: updater(prev[eventKey]),
    }));
  };

  const setDraftShiftMode = (eventKey, splitMode) => {
    updateDraft(eventKey, current => {
      const wasSplitMode = Boolean(current.allow_multiple_shifts);
      if (wasSplitMode === splitMode) return current;

      return {
        ...current,
        allow_multiple_shifts: splitMode,
        entries: (current.entries || []).map(entry => {
          if (splitMode) {
            const total = parseMoney(entry.earnings);
            return {
              ...entry,
              earnings_midday: "",
              earnings_evening: formatMoneyInput(total),
              earnings: formatMoneyInput(total),
            };
          }

          const total = parseMoney(entry.earnings_midday) + parseMoney(entry.earnings_evening);
          return {
            ...entry,
            earnings: formatMoneyInput(total),
          };
        }),
      };
    });
  };

  const addEventTable = () => {
    const event = createEmptyEvent(year);
    setEvents(prev => [...prev, event]);
    setEditingEventKey(event._draftId);
    setEventDrafts(prev => ({ ...prev, [event._draftId]: normalizeEventForDraft(event) }));
  };

  const addEntry = (eventKey) => {
    updateDraft(eventKey, draft => ({
      ...draft,
      entries: [...(draft.entries || []), createEmptyEntry(year)],
    }));
  };

  const removeDraftEntry = (eventKey, entryKey) => {
    updateDraft(eventKey, draft => ({
      ...draft,
      entries: (draft.entries || []).filter(entry => (entry.id || entry._draftId) !== entryKey),
    }));
  };

  const saveEventTable = async (event) => {
    const key = event.id || event._draftId;
    const draft = eventDrafts[key];
    if (!draft?.name?.trim()) {
      showNotification("error", "Der Eventname ist erforderlich.");
      return;
    }

    const sortedEntries = [...(draft.entries || [])].sort((a, b) => String(a.work_date || "").localeCompare(String(b.work_date || "")));
    const firstDate = sortedEntries[0]?.work_date || draft.start_date || `${year}-01-01`;
    const lastDate = sortedEntries[sortedEntries.length - 1]?.work_date || draft.end_date || firstDate;
    const eventPayload = {
      name: draft.name.trim(),
      start_date: firstDate,
      end_date: lastDate,
      allow_multiple_shifts: Boolean(draft.allow_multiple_shifts),
    };

    try {
      let eventId = event.id;
      if (eventId) {
        await apiFetch(`/api/zeit/events/${eventId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(eventPayload),
        });
      } else {
        const created = await apiFetch("/api/zeit/events", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(eventPayload),
        });
        eventId = created.id;
      }

      const existingIds = new Set((event.entries || []).map(entry => Number(entry.id)));
      const savedIds = new Set();
      for (const entry of sortedEntries) {
        if (!entry.work_date) continue;
        const payload = prepareEntryPayload(entry, eventPayload.allow_multiple_shifts);
        if (entry.id) {
          await apiFetch(`/api/zeit/events/${eventId}/entries/${entry.id}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
          savedIds.add(Number(entry.id));
        } else {
          const saved = await apiFetch(`/api/zeit/events/${eventId}/entries`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
          savedIds.add(Number(saved.id));
        }
      }

      for (const existingId of existingIds) {
        if (!savedIds.has(existingId)) {
          await apiFetch(`/api/zeit/events/${eventId}/entries/${existingId}`, { method: "DELETE" });
        }
      }

      await loadEvents();
      setEditingEventKey(null);
      setSettingsEventKey(null);
      setEventDrafts(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      showNotification("success", "Zeittabelle wurde gespeichert.");
    } catch (err) {
      console.error(err);
      if (!err.sessionExpired) showNotification("error", err.message);
    }
  };

  const deleteEventTable = async (event) => {
    if (event._draftId) {
      cancelEditEvent(event);
      return;
    }
    if (!window.confirm(`Soll "${event.name}" wirklich gelöscht werden?`)) return;
    try {
      await apiFetch(`/api/zeit/events/${event.id}`, { method: "DELETE" });
      setEvents(prev => prev.filter(item => Number(item.id) !== Number(event.id)));
      showNotification("success", "Zeittabelle wurde gelöscht.");
    } catch (err) {
      console.error(err);
      if (!err.sessionExpired) showNotification("error", err.message);
    }
  };

  const closeNotes = () => {
    setNotesEntry(null);
    setNotesDraft("");
    setNotesReadOnly(false);
  };

  const openNotes = (eventKey, entry, readOnly = false) => {
    setNotesEntry({ eventKey, entryKey: entry.id || entry._draftId });
    setNotesDraft(entry.notes || "");
    setNotesReadOnly(readOnly);
  };

  const saveNotes = () => {
    if (!notesEntry || notesReadOnly) return;
    updateDraft(notesEntry.eventKey, draft => ({
      ...draft,
      entries: (draft.entries || []).map(entry => (
        (entry.id || entry._draftId) === notesEntry.entryKey ? { ...entry, notes: notesDraft } : entry
      )),
    }));
    closeNotes();
  };

  const getEntryMoney = (entry, splitMode) => {
    if (!splitMode) return parseMoney(entry.earnings);
    return parseMoney(entry.earnings_midday) + parseMoney(entry.earnings_evening);
  };

  const calculateTotals = (entries, splitMode) => entries.reduce((acc, entry) => {
    acc.minutes += calculateMinutes(entry);
    acc.midday += parseMoney(entry.earnings_midday);
    acc.evening += parseMoney(entry.earnings_evening);
    acc.earnings += getEntryMoney(entry, splitMode);
    return acc;
  }, { minutes: 0, midday: 0, evening: 0, earnings: 0 });

  const renderEventTable = (event) => {
    const key = event.id || event._draftId;
    const isEditing = editingEventKey === key;
    const draft = isEditing ? eventDrafts[key] : normalizeEventForDraft(event);
    const entries = [...(draft?.entries || [])].sort((a, b) => String(a.work_date || "").localeCompare(String(b.work_date || "")));
    const splitMode = Boolean(draft?.allow_multiple_shifts);
    const totals = calculateTotals(entries, splitMode);
    const hourlyTotal = totals.minutes > 0 ? totals.earnings / (totals.minutes / 60) : 0;
    const settingsOpen = settingsEventKey === key;

    return (
      <section className="time-year-table" key={key}>
        <div className="time-table-titlebar">
          <div className="time-table-left-actions">
            <button
              type="button"
              className="time-table-corner-button"
              onClick={() => setSettingsEventKey(settingsOpen ? null : key)}
              aria-label="Tabelleneinstellungen"
            >
              <FaCog />
            </button>
          </div>
          {isEditing ? (
            <input
              className="time-table-name-input"
              value={draft.name || ""}
              onChange={e => updateDraft(key, current => ({ ...current, name: e.target.value }))}
            />
          ) : (
            <h3>{event.name}</h3>
          )}
          <div className="time-table-right-actions">
            {isEditing ? (
              <>
                <button type="button" className="time-table-corner-button" onClick={() => saveEventTable(event)} aria-label="Speichern">
                  <FaCheck />
                </button>
                <button type="button" className="time-table-corner-button danger" onClick={() => cancelEditEvent(event)} aria-label="Abbrechen">
                  <FaTimes />
                </button>
              </>
            ) : (
              <button type="button" className="time-table-corner-button" onClick={() => startEditEvent(event)} aria-label="Bearbeiten">
                <FaPen />
              </button>
            )}
          </div>
        </div>

        {settingsOpen && (
          <div className="time-table-settings">
            <label>
              <input
                type="radio"
                checked={!splitMode}
                disabled={!isEditing}
                onChange={() => setDraftShiftMode(key, false)}
              />
              eine Schicht
            </label>
            <label>
              <input
                type="radio"
                checked={splitMode}
                disabled={!isEditing}
                onChange={() => setDraftShiftMode(key, true)}
              />
              zwei Schichten
            </label>
            {isEditing && (
              <button type="button" className="danger" onClick={() => deleteEventTable(event)}>
                <FaTrash /> Tabelle löschen
              </button>
            )}
          </div>
        )}

        <div className="time-table-scroll">
          <table className="time-table">
            <thead>
              <tr>
                <th>Datum</th>
                <th>Startzeit</th>
                <th>Endzeit</th>
                <th>Gesamtstunden</th>
                {splitMode ? (
                  <>
                    <th>Mittags</th>
                    <th>Abends</th>
                  </>
                ) : null}
                <th>Tagesverdienst</th>
                <th>Stundenlohn</th>
                <th>Info</th>
                {isEditing && <th></th>}
              </tr>
            </thead>
            <tbody>
              {entries.map(entry => {
                const entryKey = entry.id || entry._draftId;
                const minutes = calculateMinutes(entry);
                const dayMoney = getEntryMoney(entry, splitMode);
                const hourly = minutes > 0 ? dayMoney / (minutes / 60) : 0;
                const hasNotes = Boolean(String(entry.notes || "").trim());
                return (
                  <tr key={entryKey}>
                    <td data-label="Datum">
                      {isEditing ? (
                        <input
                          type="date"
                          value={entry.work_date || ""}
                          onChange={e => updateDraft(key, current => ({
                            ...current,
                            entries: current.entries.map(item => (
                              (item.id || item._draftId) === entryKey ? { ...item, work_date: e.target.value } : item
                            )),
                          }))}
                        />
                      ) : formatDate(entry.work_date)}
                    </td>
                    <td data-label="Startzeit">
                      {isEditing ? (
                        <input
                          type="time"
                          value={entry.start_time || ""}
                          onChange={e => updateDraft(key, current => ({
                            ...current,
                            entries: current.entries.map(item => (
                              (item.id || item._draftId) === entryKey ? { ...item, start_time: e.target.value } : item
                            )),
                          }))}
                        />
                      ) : entry.start_time || "-"}
                    </td>
                    <td data-label="Endzeit">
                      {isEditing ? (
                        <input
                          type="time"
                          value={entry.end_time || ""}
                          onChange={e => updateDraft(key, current => ({
                            ...current,
                            entries: current.entries.map(item => (
                              (item.id || item._draftId) === entryKey ? { ...item, end_time: e.target.value } : item
                            )),
                          }))}
                        />
                      ) : entry.end_time || "-"}
                    </td>
                    <td data-label="Gesamtstunden">{formatHours(minutes)}</td>
                    {splitMode && (
                      <>
                        <td data-label="Mittags">
                          {isEditing ? (
                            <input
                              type="text"
                              inputMode="decimal"
                              value={entry.earnings_midday ?? ""}
                              onChange={e => updateDraft(key, current => ({
                                ...current,
                                entries: current.entries.map(item => (
                                  (item.id || item._draftId) === entryKey ? { ...item, earnings_midday: e.target.value } : item
                                )),
                              }))}
                            />
                          ) : formatMoney(entry.earnings_midday)}
                        </td>
                        <td data-label="Abends">
                          {isEditing ? (
                            <input
                              type="text"
                              inputMode="decimal"
                              value={entry.earnings_evening ?? ""}
                              onChange={e => updateDraft(key, current => ({
                                ...current,
                                entries: current.entries.map(item => (
                                  (item.id || item._draftId) === entryKey ? { ...item, earnings_evening: e.target.value } : item
                                )),
                              }))}
                            />
                          ) : formatMoney(entry.earnings_evening)}
                        </td>
                      </>
                    )}
                    <td data-label="Tagesverdienst">
                      {isEditing && !splitMode ? (
                        <input
                          type="text"
                          inputMode="decimal"
                          value={entry.earnings ?? ""}
                          onChange={e => updateDraft(key, current => ({
                            ...current,
                            entries: current.entries.map(item => (
                              (item.id || item._draftId) === entryKey ? { ...item, earnings: e.target.value } : item
                            )),
                          }))}
                        />
                      ) : formatMoney(dayMoney)}
                    </td>
                    <td data-label="Stundenlohn">{formatMoney(hourly)}/h</td>
                    <td data-label="Info">
                      <button
                        type="button"
                        className={`time-note-button ${hasNotes ? "has-notes" : ""}`}
                        onClick={() => openNotes(key, entry, !isEditing)}
                        aria-label="Notizen"
                      >
                        <FaInfo />
                      </button>
                    </td>
                    {isEditing && (
                      <td data-label="Aktion">
                        <button type="button" className="time-icon-button danger" onClick={() => removeDraftEntry(key, entryKey)}>
                          <FaTrash />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {isEditing && (
                <tr className="time-add-row">
                  <td colSpan={splitMode ? 9 : 7}>
                    <button type="button" className="time-add-day-button" onClick={() => addEntry(key)}>
                      <FaPlus /> Tag hinzufügen
                    </button>
                  </td>
                  <td></td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="3" data-label="Zusammenfassung">Gesamt</td>
                <td data-label="Gesamtstunden">{formatHours(totals.minutes)}</td>
                {splitMode && (
                  <>
                    <td data-label="Mittags">{formatMoney(totals.midday)}</td>
                    <td data-label="Abends">{formatMoney(totals.evening)}</td>
                  </>
                )}
                <td data-label="Verdienst">{formatMoney(totals.earnings)}</td>
                <td data-label="Stundenlohn">{formatMoney(hourlyTotal)}/h</td>
                <td className="time-summary-empty"></td>
                {isEditing && <td className="time-summary-empty"></td>}
              </tr>
            </tfoot>
          </table>
        </div>
      </section>
    );
  };

  return (
    <div className="time-section">
      <div className="time-year-header">
        <button type="button" onClick={() => setYear(prev => prev - 1)} aria-label="Vorjahr">
          <FaArrowLeft />
        </button>
        <strong>{year}</strong>
        <button type="button" onClick={() => setYear(prev => prev + 1)} aria-label="Nächstes Jahr">
          <FaArrowRight />
        </button>
      </div>
      <div className="time-toolbar">
        <button type="button" onClick={addEventTable}>
          <FaPlus /> Tabelle erstellen
        </button>
      </div>
      <div className="time-year-list">
        {loading && <p>Lädt...</p>}
        {!loading && visibleEvents.length === 0 && (
          <p className="time-empty-state">Für {year} sind noch keine Zeittabellen vorhanden.</p>
        )}
        {visibleEvents.map(renderEventTable)}
      </div>
      {notesEntry && (
        <div className="time-modal-backdrop" onClick={closeNotes}>
          <div className="time-notes-modal" onClick={e => e.stopPropagation()}>
            <h3>Notizen</h3>
            <textarea
              value={notesDraft}
              onChange={e => setNotesDraft(e.target.value)}
              readOnly={notesReadOnly}
              placeholder={notesReadOnly ? "Keine Notiz vorhanden" : "Notiz hinzufügen"}
            />
            <div>
              <button type="button" onClick={closeNotes}>{notesReadOnly ? "Schließen" : "Abbrechen"}</button>
              {!notesReadOnly && (
                <button type="button" onClick={saveNotes}><FaCheck /> Speichern</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeTrackingSection;
